/**
 * CDM Daily Report — printable rollup of the whole CDM state for one day.
 *
 * Renders /api/cdm/report?format=markdown as a live board. Admins can
 * also fan it out to Slack / Discord / Teams via /api/cdm/report/send
 * (uses the existing frontier-digest webhook layer).
 *
 * This is the natural board-friendly view that auditors and the Group
 * CISO want to see in one PDF. Phase 10.5 wires PDF export.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  FileText, RefreshCw, ChevronLeft, Send, Copy, Download,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface ReportResponse {
  markdown: string
  json: any
}

export default function CdmReportPage() {
  const [data, setData] = useState<ReportResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [message, setMessage] = useState('')

  const load = () => {
    setLoading(true)
    api.get('/api/cdm/report', { params: { format: 'markdown' } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  const send = async () => {
    setSending(true)
    setMessage('Sending to webhooks…')
    try {
      const { data: r } = await api.post('/api/cdm/report/send')
      const sent = r.sent?.length ? r.sent.join(', ') : 'no targets configured'
      const failed = r.failed?.length ? ` · failed: ${r.failed.join(', ')}` : ''
      setMessage(`Sent to: ${sent}${failed} (${r.preview_chars} chars)`)
    } catch (e: any) {
      setMessage(`Send failed: ${e.response?.data?.detail || 'unknown'}`)
    } finally {
      setSending(false)
    }
  }

  const copyMarkdown = async () => {
    if (!data?.markdown) return
    try {
      await navigator.clipboard.writeText(data.markdown)
      setMessage('Markdown copied to clipboard.')
    } catch {
      setMessage('Copy failed (clipboard API unavailable).')
    }
  }

  const downloadMarkdown = () => {
    if (!data?.markdown) return
    const blob = new Blob([data.markdown], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `cdm-report-${new Date().toISOString().slice(0, 10)}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <FileText size={18} className="text-blue-light" /> CDM DAILY REPORT
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          assets · risk · compliance · vendor concentration · adapter health
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={copyMarkdown} disabled={!data?.markdown} className="btn-ghost" title="Copy markdown">
            <Copy size={11} /> Copy
          </button>
          <button type="button" onClick={downloadMarkdown} disabled={!data?.markdown} className="btn-ghost" title="Download as .md">
            <Download size={11} /> Download
          </button>
          <button type="button" onClick={send} disabled={sending} className="btn-ghost"
                  title="Fan-out to configured Slack/Discord/Teams webhooks (admin only)">
            <Send size={11} /> {sending ? 'Sending…' : 'Send'}
          </button>
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {message && (
        <div className="text-[11px] text-text-secondary font-mono px-3 py-2 border border-border rounded bg-surface-card">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Markdown source */}
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-text-primary mb-2">Markdown (raw)</h2>
          <pre className="text-[11px] font-mono text-text-secondary whitespace-pre-wrap leading-relaxed bg-surface-raised p-3 rounded overflow-auto max-h-[700px]">
            {data?.markdown || (loading ? 'Loading…' : 'No data yet.')}
          </pre>
        </div>

        {/* Rendered preview */}
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-text-primary mb-2">Preview</h2>
          {data?.markdown ? (
            <div className="prose prose-sm prose-invert max-w-none text-xs leading-relaxed">
              <MarkdownPreview md={data.markdown} />
            </div>
          ) : (
            <p className="text-text-muted text-sm">No data yet.</p>
          )}
        </div>
      </div>

      <p className="text-[10px] text-text-muted text-center">
        Send fans out to SLACK_WEBHOOK_URL / DISCORD_WEBHOOK_URL / TEAMS_WEBHOOK_URL (whichever are
        set on the backend). Phase 10.5 will wire scheduled daily delivery via Celery beat.
      </p>
    </div>
  )
}


// Lightweight markdown renderer — just enough for our report shape.
// Avoids pulling react-markdown for one page.
function MarkdownPreview({ md }: { md: string }) {
  const lines = md.split('\n')
  const elements: React.ReactNode[] = []
  let tableRows: string[][] = []
  let inTable = false

  const flushTable = () => {
    if (!inTable || tableRows.length === 0) return
    const [header, _div, ...body] = tableRows
    elements.push(
      <table key={`t${elements.length}`} className="w-full text-[11px] border-collapse my-2">
        <thead className="bg-surface-raised">
          <tr>{header.map((h, i) => <th key={i} className="px-2 py-1 text-left text-text-secondary font-semibold border border-border">{h}</th>)}</tr>
        </thead>
        <tbody>
          {body.map((row, ri) => (
            <tr key={ri} className="hover:bg-surface-raised/40">
              {row.map((c, ci) => <td key={ci} className="px-2 py-1 border border-border font-mono">{renderInline(c)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    )
    tableRows = []
    inTable = false
  }

  for (const line of lines) {
    if (line.startsWith('|')) {
      inTable = true
      tableRows.push(line.split('|').slice(1, -1).map(c => c.trim()))
      continue
    }
    flushTable()
    if (line.startsWith('# ')) {
      elements.push(<h1 key={elements.length} className="text-lg font-bold text-text-primary mt-2">{line.slice(2)}</h1>)
    } else if (line.startsWith('## ')) {
      elements.push(<h2 key={elements.length} className="text-sm font-semibold text-text-primary mt-3 mb-1">{line.slice(3)}</h2>)
    } else if (line.startsWith('- ')) {
      elements.push(<div key={elements.length} className="text-text-secondary ml-2">• {renderInline(line.slice(2))}</div>)
    } else if (line.startsWith('_') && line.endsWith('_')) {
      elements.push(<div key={elements.length} className="text-text-muted italic">{line.slice(1, -1)}</div>)
    } else if (line.trim()) {
      elements.push(<div key={elements.length} className="text-text-secondary">{renderInline(line)}</div>)
    }
  }
  flushTable()
  return <>{elements}</>
}

function renderInline(s: string): React.ReactNode {
  // Replace **bold** with <strong>
  const parts = s.split(/(\*\*[^*]+\*\*|`[^`]+`)/)
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={i} className="text-text-primary font-bold">{p.slice(2, -2)}</strong>
    }
    if (p.startsWith('`') && p.endsWith('`')) {
      return <code key={i} className="text-blue-light bg-surface-raised px-1 rounded">{p.slice(1, -1)}</code>
    }
    return p
  })
}
